import { Box, Stack } from "@mui/material";

import { useLocaleContext } from "@/contexts/LocaleContext";
import { getDate, shouldDisplayYear } from "@/forms-design-system/FormDate/helper";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Trans, useTranslate } from "@/hooks/useTranslate";

type FormDateSelectItemLabelProps = {
  startDate: Date | number;
  endDate: Date | number;
  isSoldOut?: boolean;
  isClosed?: boolean;
  wrapOnMobile?: boolean;
};

export const FormDateSelectItemLabel = ({
  startDate,
  endDate,
  isSoldOut = false,
  isClosed = false,
  wrapOnMobile = false,
}: FormDateSelectItemLabelProps) => {
  const { isoLocale } = useLocaleContext();
  const { t } = useTranslate();
  const { isSmallScreen } = useMediaQuery();

  const renderLabel = () => {
    const luxonStartDate = getDate(startDate);
    const luxonEndDate = getDate(endDate);
    const isMultiDayDate = !luxonStartDate.startOf("day").equals(luxonEndDate.startOf("day"));

    if (isMultiDayDate) {
      if (isSmallScreen && wrapOnMobile) {
        return (
          <Stack gap={1} width="100%">
            <FormTypography variant="subtitle2" component="span">
              {luxonStartDate.toLocaleString(
                {
                  year: shouldDisplayYear(luxonStartDate) ? "numeric" : undefined,
                  month: "short",
                  day: "numeric",
                  weekday: "short",
                  hour: "numeric",
                  minute: "2-digit",
                },
                { locale: isoLocale }
              )}
              &nbsp;→&nbsp;
            </FormTypography>
            <FormTypography variant="subtitle2" component="span">
              {luxonEndDate.toLocaleString(
                {
                  year: shouldDisplayYear(luxonStartDate) ? "numeric" : undefined,
                  month: "short",
                  day: "numeric",
                  weekday: "short",
                  hour: "numeric",
                  minute: "2-digit",
                },
                { locale: isoLocale }
              )}
            </FormTypography>
          </Stack>
        );
      }

      return (
        <Trans
          space="common"
          i18nKey="dateSelectItemMultiDay"
          components={{
            sb: <FormTypography variant="subtitle2" component="span" />,
          }}
          values={{
            startDate: luxonStartDate.toLocaleString(
              {
                year: shouldDisplayYear(luxonStartDate) ? "numeric" : undefined,
                month: "short",
                day: "numeric",
                weekday: "short",
              },
              { locale: isoLocale }
            ),
            startTime: luxonStartDate.toLocaleString({ hour: "numeric", minute: "2-digit" }, { locale: isoLocale }),
            endDate: luxonEndDate.toLocaleString(
              {
                year: shouldDisplayYear(luxonEndDate) ? "numeric" : undefined,
                month: "short",
                day: "numeric",
                weekday: "short",
              },
              { locale: isoLocale }
            ),
            endTime: luxonEndDate.toLocaleString({ hour: "numeric", minute: "2-digit" }, { locale: isoLocale }),
          }}
        />
      );
    }

    return (
      <Trans
        space="common"
        i18nKey="dateSelectItemSingleDay"
        components={{
          sb: <FormTypography variant="subtitle2" component="span" />,
        }}
        values={{
          startDate: luxonStartDate.toLocaleString(
            {
              year: shouldDisplayYear(luxonStartDate) ? "numeric" : undefined,
              month: "short",
              day: "numeric",
              weekday: "short",
            },
            { locale: isoLocale }
          ),
          startTime: luxonStartDate.toLocaleString({ hour: "numeric", minute: "2-digit" }, { locale: isoLocale }),
          endTime: luxonEndDate.toLocaleString({ hour: "numeric", minute: "2-digit" }, { locale: isoLocale }),
        }}
      />
    );
  };

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} width="100%">
      <Box
        sx={{
          flex: "1 1 0%",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {renderLabel()}
      </Box>
      {isClosed && (
        <FormTypography variant="caption" sx={{ flexShrink: 0 }}>
          {t("ticketing", "closed")}
        </FormTypography>
      )}
      {isSoldOut && !isClosed && (
        <FormTypography variant="caption" sx={{ flexShrink: 0 }}>
          {t("ticketing", "soldOut")}
        </FormTypography>
      )}
    </Stack>
  );
};
